// Porady pogłębione jako zadanie w tle + odpytywanie (karta ASYNC-3, 19.10.2026) — WSPÓLNE dla obu UI
// (frontend/src/pages/AdvicePage.jsx i frontend/src/ui-cockpit/views/AdviceView.jsx). Strony porad są zamrożone, więc
// przełączenie robi `lib/api.js`: `deepAdvice`, `deepAdviceWithFile`, `taxOptimization`, `taxPrediction` zachowały
// nazwy i kształt wyniku (`{ data }` jak z axiosa), ale przy włączonej fladze serwera idą trasą `/async`.
//
// Rdzeń odpytywania to ten sam co w Kreatorze (`generacjaAsync.js`): błąd sieci/bramy/5xx w trakcie odpytywania = pytaj
// dalej (NIGDY nie startuj drugi raz), 410 (wynik już odebrany) i przekroczenie czasu klienta = `ECONNABORTED` (strony
// porad robią wtedy salvage z historii porad: `trySalvageAdvice`, porada jest zapisywana w `advice_history`), błąd
// zadania = błąd w kształcie axios (`humanizeApiError`). Różnice względem Kreatora:
//   * flaga to `porada_async` z `GET /flags` (osobna od `kreator_async`),
//   * start jest idempotentny PO STRONIE SERWERA (skrót danych): ponowny start z tymi samymi danymi dołącza do trwającej
//     porady (202 + `dolaczono`), więc klient może bezpiecznie ponowić start po błędzie bramy (do 2 razy) i nie
//     potrzebuje pamięci payloadów; 409 oznacza trwającą poradę Z INNYMI danymi → czytelny komunikat, formularz nietknięty,
//   * odpytywanie co 5 s (porada trwa 400–540 s).
import { czekajNaZadanie, czyBladBramy } from "./generacjaAsync";

export const INTERWAL_PORADY_MS = 5000;
/** PORADA-TLE (27.10.2026): porada szybka trwa 1-3 min — odpytywanie co 3 s (swiezosc wskaznika postepu). */
export const INTERWAL_PORADY_SZYBKIEJ_MS = 3000;
export const LICZBA_PONOWIEN_STARTU = 2;
export const PAUZA_PONOWIENIA_MS = 3000;

let pamiecFlagi = null;

/** Czy porady idą trasami async (flaga z `GET /flags`). Błąd odczytu = false (klasycznie) i nie jest zapamiętywany. */
export async function czyPoradaAsync(pobierzFlagi) {
  if (pamiecFlagi !== null) return pamiecFlagi;
  try {
    const res = await pobierzFlagi();
    pamiecFlagi = res?.data?.porada_async === true;
    return pamiecFlagi;
  } catch {
    return false;
  }
}

/** Do testów i do wymuszenia ponownego odczytu flagi. */
export function ustawPamiecFlagiPoradaAsync(wartosc) {
  pamiecFlagi = wartosc;
}

const uspij = (ms) => new Promise((r) => setTimeout(r, ms));

/** 409 (trwa już taka porada z innymi danymi): czytelny komunikat zamiast obiektu `detail`, formularz zostaje. */
function bladKonfliktu(err) {
  const r = err.response;
  const szczegol = r?.data?.detail;
  const komunikat = (typeof szczegol === "string" ? szczegol : szczegol?.message)
    || "Taka porada już trwa. Poczekaj na jej zakończenie.";
  err.response = { ...r, data: { detail: `${komunikat} Twoje dane nie zostały usunięte.` } };
  return err;
}

/**
 * Start zadania; zwraca job_id albo `null`, gdy trasa async jest niedostępna (404 „Not Found” = flaga wyłączona po
 * stronie serwera). Błąd bramy/sieci przy starcie: ponów (serwer dołączy do już utworzonego zadania, jeśli poprzedni
 * start jednak je utworzył).
 */
async function uruchom(start, opcje) {
  const ponowienia = opcje.ponowienia ?? LICZBA_PONOWIEN_STARTU;
  const pauza = opcje.pauzaPonowieniaMs ?? PAUZA_PONOWIENIA_MS;
  const spij = opcje.uspij ?? uspij;
  for (let proba = 0; ; proba++) {
    try {
      const res = await start();
      const jobId = res?.data?.job_id;
      if (!jobId) throw new Error("Serwer nie zwrócił identyfikatora zadania porady.");
      return jobId;
    } catch (err) {
      const r = err?.response;
      if (r?.status === 404 && r?.data?.detail === "Not Found") return null; // flaga wyłączona po stronie serwera
      if (r?.status === 409) throw bladKonfliktu(err);
      if (czyBladBramy(err) && err?.code !== "ECONNABORTED" && proba < ponowienia) {
        await spij(pauza);
        continue;
      }
      throw err;
    }
  }
}

/**
 * Porada: gdy `asyncWlaczone` — jako zadanie w tle z odpytywaniem; inaczej (albo gdy trasa async jest niedostępna)
 * `klasycznie()`. Zwraca `{ data }` jak klasyczna trasa. `start()` → odpowiedź 202 z `job_id`; `odczytaj(jobId)` → odpowiedź
 * GET statusu.
 * PORADA-TLE (27.10.2026): `przyStarcie(jobId)` — wołane RAZ, gdy zadanie w tle naprawdę istnieje (po 202 albo po 409 z
 * dołączeniem; NIGDY w trybie klasycznym), `przyOdczycie(res)` — po KAŻDYM udanym odczycie statusu (także bez zmiany
 * etapu; „serwer odpowiada”), `przyPostepie(postep)` — jak dotąd, gdy odpowiedź niesie `postep`. Wskaźnik postępu
 * klasyka/cockpitu (`lib/postepPorady.js`) liczy z nich czas od startu i świeżość.
 */
export async function poradaAuto({ asyncWlaczone, start, odczytaj, klasycznie, przyStarcie, przyOdczycie, ...opcje }) {
  if (!asyncWlaczone) return klasycznie();
  const jobId = await uruchom(start, opcje);
  if (jobId === null) {
    pamiecFlagi = false;
    return klasycznie();
  }
  if (typeof przyStarcie === "function") {
    try { przyStarcie(jobId); } catch { /* wskaźnik jest tylko ozdobą */ }
  }
  const odczyt = typeof przyOdczycie === "function"
    ? async () => {
      const res = await odczytaj(jobId);
      try { przyOdczycie(res); } catch { /* wskaźnik jest tylko ozdobą */ }
      return res;
    }
    : () => odczytaj(jobId);
  return czekajNaZadanie(odczyt, { interwalMs: INTERWAL_PORADY_MS, ...opcje });
}
